/**
 * External Chat tab for the collection page.
 * Shows embed config panel, API key management, agent instructions editor,
 * live preview iframe, and copy-to-clipboard code snippet.
 *
 * When no API keys exist yet, displays a centered CTA card prompting the user
 * to generate their first embed key before accessing the full configuration.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  KeyRound,
  Loader2,
  ExternalLink,
  History,
  MessageSquareShare,
  RotateCcw,
  Save,
  ChevronDown,
  Copy,
  Check,
  X,
} from 'lucide-react';
// External chat filter configuration lives on the new-schema top-level
// `collection.external_chat_filters` field, edited via the Edit Collection
// modal's ExternalChatFiltersSection. The iframe hydrates facet values live
// on mount, so nothing in this tab needs to write filter definitions.
import * as Sentry from '@sentry/react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useNotification } from '@/components/notifications';
import { ApiDocsSection } from './api-docs-section';
import {
  ManageKeysModal,
  RevealNewKeyModal,
  RevokeKeyModal,
} from './components/api-keys-modals';
import {
  ActivateVersionModal,
  DeleteVersionModal,
  InstructionVersionsModal,
  SaveInstructionVersionModal,
} from './components/instruction-versions-modal';
import { ColorField, FontField, InfoTip } from './components/form-fields';
import { validateGoogleFont } from './helpers/validate-google-font';
import { buildEmbedSnippet, buildPreviewUrl } from './helpers/build-embed-url';
import {
  isValidFrameAncestorHost,
  normalizeFrameAncestorToken,
  parseFrameAncestorsInput,
} from '@edge/frame-ancestor-hosts';
import {
  addForbiddenKeyword,
  popLastForbiddenKeyword,
  removeForbiddenKeyword,
} from './helpers/forbidden-keywords';
import { SearchQueryExportSection } from './components/search-query-export-section';

import { cn } from '@/lib/utils';
import { apiRequest } from '@/utils/api-request';
import { updateCollection } from '@/services/data-sources/update-collection';
import { ExternalChatApiError } from '@/services/external-chat/detail-error';
import { useGetCollectionInfo } from '@/hooks/queries/data-sources/use-get-collection-info';
import { useEmbedKeys } from '@/hooks/queries/external-chat/use-embed-keys';
import { useExternalChatConfig } from '@/hooks/queries/external-chat/use-external-chat-config';
import { useInstructionVersions } from '@/hooks/queries/external-chat/use-instruction-versions';
import {
  useActivateInstructionVersion,
  useCreateEmbedKey,
  useCreateInstructionVersion,
  useDeleteInstructionVersion,
  useRevokeEmbedKey,
  useSaveExternalChatConfig,
  useUpdateEmbedKeySettings,
} from '@/hooks/mutations/external-chat/use-external-chat-mutations';
import { ExternalChatFiltersSection } from '@/pages/data-sources/external-chat-filters-section';
import type {
  EmbedKeySettings,
  ExternalChatConfig,
  PromptVersion,
} from '@/services/external-chat/types';
import type { ExternalChatFilterDef } from '@/types/source-types';

interface ExternalChatTabProps {
  collectionId: string;
  collectionName: string;
  orgId: string;
  externalChatFilters?: ExternalChatFilterDef[] | null;
  /** Fired after a successful save so the parent can refresh its collection cache. */
  onCollectionUpdated?: () => void;
}

const DEFAULT_INSTRUCTIONS_PREFIX = 'You are a helpful assistant';

/** Seed for the config draft before the server payload arrives, and the base
 *  the fetched config is merged onto so unset fields keep a usable value. */
const DEFAULT_CONFIG: Partial<ExternalChatConfig> = {
  mode: 'bubble',
  primary_color: '#2563eb',
  font_color: '#111827',
  background_color: '#ffffff',
  link_color: '#2563eb',
  greeting: 'Ask me anything!',
  title: '',
  custom_instructions: '',
  summary_instructions_mode: 'append',
  forbidden_topic_keywords: [],
};

/** Sentinel used in the AI Model Select to represent "no model chosen" (= default Claude Sonnet). */
const DEFAULT_MODEL_VALUE = '__default__';

type LlmModelOption = {
  label: string;
  value: string;
  provider?: string;
  wire_id?: string;
  external_chat_compatible?: boolean;
};

type LlmModelsResponse = {
  openai: LlmModelOption[];
  anthropic: LlmModelOption[];
};

/**
 * Collapsible config section with a consistent visual boundary: the whole
 * section is wrapped in a bordered container, the trigger is the header, and
 * the content sits below a divider with indented padding. This makes it
 * obvious at a glance which fields belong to which section.
 *
 * Validation states inside a collapsed section would otherwise be invisible
 * (Radix unmounts collapsed content). So the section:
 *   - shows a red border + dot on the header when `hasError` is set, surfacing
 *     the problem even while collapsed; and
 *   - force-opens whenever `openSignal` changes to a new value, letting the
 *     parent expand the section on a failed save so the error is reachable.
 */
const ConfigSection = ({
  title,
  defaultOpen,
  hasError,
  openSignal,
  children,
}: {
  title: ReactNode;
  defaultOpen?: boolean;
  hasError?: boolean;
  openSignal?: number;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen ?? false);

  // Re-open whenever the parent bumps openSignal (e.g. failed save with an
  // error living inside this section). Skip the initial 0/undefined value so
  // we don't override defaultOpen on mount.
  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn('rounded-lg border', hasError && 'border-destructive')}
    >
      <CollapsibleTrigger className='flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-muted/50 [&[data-state=open]>svg]:rotate-180'>
        <span className='flex items-center gap-2'>
          {title}
          {hasError && (
            <span
              className='size-2 shrink-0 rounded-full bg-destructive'
              aria-label='Section has validation errors'
            />
          )}
        </span>
        <ChevronDown className='size-4 text-muted-foreground transition-transform' />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className='space-y-4 border-t px-3 py-4'>{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const ExternalChatTab = ({
  collectionId,
  collectionName,
  orgId,
  externalChatFilters,
  onCollectionUpdated,
}: ExternalChatTabProps) => {
  const notification = useNotification();

  // Live Qdrant payload indexes for this collection — source of truth for
  // which fields can be surfaced as iframe filters. Independent of
  // `exposed_upload_attributes` (upload-editor scope).
  const {
    data: collectionInfo,
    isLoading: collectionInfoLoading,
    isError: collectionInfoError,
  } = useGetCollectionInfo({ collectionId, orgId });

  const availableFilterFields = useMemo(
    () => Object.keys(collectionInfo?.payload_schema ?? {}).sort(),
    [collectionInfo?.payload_schema],
  );

  // Filters draft (mirrors collection.external_chat_filters). Dirty flag tracks
  // unsaved edits so the existing Save Configuration button can persist them.
  const [filtersDraft, setFiltersDraft] = useState<ExternalChatFilterDef[]>(
    externalChatFilters ?? [],
  );
  const [filtersDirty, setFiltersDirty] = useState(false);

  // Re-seed from parent when the upstream collection changes (e.g. after save
  // or navigating between collections).
  useEffect(() => {
    setFiltersDraft(externalChatFilters ?? []);
    setFiltersDirty(false);
  }, [externalChatFilters]);

  const handleFiltersChange = useCallback((next: ExternalChatFilterDef[]) => {
    setFiltersDraft(next);
    setFiltersDirty(true);
  }, []);

  // Server state. Queries own fetching/caching; mutations invalidate them, so
  // nothing here re-derives a list by hand after a write.
  const { data: keys = [], isLoading: keysLoading } = useEmbedKeys(
    orgId,
    collectionId,
  );
  const { data: configData, isLoading: configLoading } = useExternalChatConfig(
    orgId,
    collectionId,
  );
  const {
    data: versions = [],
    isLoading: versionsLoading,
    isError: versionsLoadFailed,
    isFetching: versionsFetching,
    refetch: refetchVersions,
  } = useInstructionVersions(orgId, collectionId);

  const createKey = useCreateEmbedKey(orgId, collectionId);
  const revokeKey = useRevokeEmbedKey(orgId, collectionId);
  const updateKeySettings = useUpdateEmbedKeySettings(orgId, collectionId);
  const saveConfig = useSaveExternalChatConfig(orgId, collectionId);
  const createVersion = useCreateInstructionVersion(orgId, collectionId);
  const activateVersion = useActivateInstructionVersion(orgId, collectionId);
  const deleteVersion = useDeleteInstructionVersion(orgId, collectionId);

  const creatingKey = createKey.isPending;
  const savingConfig = saveConfig.isPending;
  const savingVersion = createVersion.isPending;
  const activatingVersionId = activateVersion.isPending
    ? (activateVersion.variables ?? null)
    : null;

  // Activation rewrites `custom_instructions` server-side; Save Configuration
  // PATCHes the whole config from the local draft. Whichever lands second
  // silently reverts the other, so neither may start while the other is in
  // flight — the editor and the server would otherwise disagree.
  const configWriteInFlight = savingConfig || activateVersion.isPending;

  // Config draft. The fetched config seeds this; every edit after that is
  // local until Save Configuration persists it.
  const [config, setConfig] =
    useState<Partial<ExternalChatConfig>>(DEFAULT_CONFIG);
  const [configDirty, setConfigDirty] = useState(false);
  const [frameAncestorsInput, setFrameAncestorsInput] = useState('');
  const [frameAncestorsError, setFrameAncestorsError] = useState<string | null>(
    null,
  );
  // Snapshot of config used for preview/snippet — only updates on save or initial load
  const [previewConfig, setPreviewConfig] =
    useState<Partial<ExternalChatConfig>>(DEFAULT_CONFIG);

  // Newly created key reveal dialog
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Revoke confirmation
  const [keyToRevoke, setKeyToRevoke] = useState<string | null>(null);

  // Snippet copy state
  const [copied, setCopied] = useState(false);

  // API keys modal state
  const [keysModalOpen, setKeysModalOpen] = useState(false);

  // Instruction version history UI state
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [saveVersionModalOpen, setSaveVersionModalOpen] = useState(false);
  const [versionNameDraft, setVersionNameDraft] = useState('');
  const [versionToDelete, setVersionToDelete] = useState<PromptVersion | null>(
    null,
  );
  const [versionToConfirmActivate, setVersionToConfirmActivate] =
    useState<PromptVersion | null>(null);

  // Font validation state
  const [fontErrors, setFontErrors] = useState<Record<string, string>>({});

  // Numeric field validation state (Search Settings). Mirrors the backend
  // Pydantic constraints in routes/external_chat.py so users get an inline
  // error instead of a 422 on save.
  const [numberErrors, setNumberErrors] = useState<Record<string, string>>({});

  // Bumped to force-open a collapsed section when a failed save has an error
  // hidden inside it.
  const [appearanceOpenSignal, setAppearanceOpenSignal] = useState(0);
  const [searchOpenSignal, setSearchOpenSignal] = useState(0);
  const [frameAncestorsOpenSignal, setFrameAncestorsOpenSignal] = useState(0);

  const hasFontErrors = Object.keys(fontErrors).length > 0;
  const hasFrameAncestorsError = frameAncestorsError !== null;
  const hasNumberErrors = Object.keys(numberErrors).length > 0;

  // Forbidden topic keyword input buffer (chip-style entry)
  const [forbiddenKeywordInput, setForbiddenKeywordInput] = useState('');

  // Filtered LLM model list for the embed surface
  const [llmModels, setLlmModels] = useState<LlmModelsResponse>({
    openai: [],
    anthropic: [],
  });

  useEffect(() => {
    let cancelled = false;
    const fetchLlmModels = async () => {
      try {
        const result = await apiRequest<
          LlmModelsResponse & { detail?: string }
        >({
          url: '/llm-models?surface=external_chat',
          method: 'GET',
          target: 'platform',
        });
        if (cancelled) return;
        // On a non-2xx response apiRequest still surfaces the parsed error
        // body in `data` alongside `detail`. Bail on `detail` and coerce the
        // provider buckets to arrays so a malformed payload can't crash the
        // tab when we later read `.length`/`.map`.
        if (result.data && !result.data.detail) {
          setLlmModels({
            openai: Array.isArray(result.data.openai) ? result.data.openai : [],
            anthropic: Array.isArray(result.data.anthropic)
              ? result.data.anthropic
              : [],
          });
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: { context: 'load-external-chat-llm-models' },
        });
      }
    };
    void fetchLlmModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFontBlur = useCallback(
    async (field: 'header_font' | 'body_font') => {
      const value = (config as Record<string, string>)[field] || '';
      const clearFieldError = (prev: Record<string, string>) => {
        const next = { ...prev };
        delete next[field];
        return next;
      };

      if (!value.trim()) {
        setFontErrors(clearFieldError);
        return;
      }
      const valid = await validateGoogleFont(value);
      setFontErrors((prev) =>
        valid
          ? clearFieldError(prev)
          : { ...prev, [field]: `"${value}" not found on Google Fonts` },
      );
    },
    [config],
  );

  // Validate a Search Settings numeric field against its backend constraint
  // and record/clear the inline error. Returns the parsed number (or null if
  // invalid) so callers can still store the raw value for display.
  const validateNumberField = useCallback(
    (
      field: string,
      raw: string,
      { min, max, integer }: { min: number; max: number; integer: boolean },
    ): boolean => {
      const clear = (prev: Record<string, string>) => {
        const next = { ...prev };
        delete next[field];
        return next;
      };
      const n = Number(raw);
      let error: string | null = null;
      if (raw.trim() === '' || Number.isNaN(n)) {
        error = 'Enter a number';
      } else if (integer && !Number.isInteger(n)) {
        error = 'Must be a whole number';
      } else if (n < min || n > max) {
        error = `Must be between ${min} and ${max}`;
      }
      setNumberErrors((prev) =>
        error ? { ...prev, [field]: error } : clear(prev),
      );
      return error === null;
    },
    [],
  );

  // Read in the seeding effect without making it a dependency — a dirty draft
  // must suppress re-seeding, but the false->true->false dirty transitions
  // themselves must not re-run it against a stale cached payload (that is the
  // revert-on-save bug).
  const configDirtyRef = useRef(configDirty);
  useEffect(() => {
    configDirtyRef.current = configDirty;
  }, [configDirty]);

  // Seed the draft from the fetched config. Runs again on every refetch
  // (post-save, post-activate, org switch) so the editor tracks the server,
  // but never while the user has unsaved edits — theirs win until they save.
  useEffect(() => {
    if (!configData?.config || configDirtyRef.current) return;
    const merged = { ...DEFAULT_CONFIG, ...configData.config };
    setConfig(merged);
    setPreviewConfig(merged);
    setFrameAncestorsInput((merged.frame_ancestors || []).join(', '));
    setFrameAncestorsError(null);
  }, [configData]);

  // An ExternalChatApiError carries a `detail` the API meant for the user, and
  // apiRequest has already reported the underlying HTTP failure. Anything else
  // is unexpected and goes to Sentry behind a generic message.
  const reportError = useCallback(
    (err: unknown, context: string, fallback: string) => {
      if (err instanceof ExternalChatApiError) {
        notification.error(err.message);
        return;
      }
      Sentry.captureException(err, { extra: { context, collectionId } });
      notification.error(fallback);
    },
    [collectionId, notification],
  );

  const handleCreateKey = useCallback(async () => {
    try {
      const key = await createKey.mutateAsync();
      setNewlyCreatedKey(key.api_key);
      setKeyCopied(false);
    } catch (err) {
      reportError(err, 'create-embed-key', 'Failed to create API key');
    }
  }, [createKey, reportError]);

  const handleRevokeKey = useCallback(
    async (apiKey: string) => {
      try {
        await revokeKey.mutateAsync(apiKey);
        notification.success('API key revoked');
      } catch (err) {
        reportError(err, 'revoke-embed-key', 'Failed to revoke API key');
      }
    },
    [notification, reportError, revokeKey],
  );

  /** Resolves true only when the PATCH actually persisted, so the caller can
   *  keep its editor open on failure instead of looking saved. */
  /** Resolves true only when the PATCH actually persisted, so the row can keep
   *  its editor open on failure instead of looking saved. */
  const handleUpdateKeySettings = useCallback(
    async (apiKey: string, settings: EmbedKeySettings): Promise<boolean> => {
      try {
        await updateKeySettings.mutateAsync({ apiKey, settings });
        notification.success('Key settings updated');
        return true;
      } catch (err) {
        reportError(
          err,
          'update-embed-key-settings',
          'Failed to update key settings',
        );
        return false;
      }
    },
    [notification, reportError, updateKeySettings],
  );

  const handleConfigChange = useCallback(
    (field: keyof ExternalChatConfig, value: unknown) => {
      setConfig((prev) => ({ ...prev, [field]: value }));
      setConfigDirty(true);
    },
    [],
  );

  // Mirror of `config` kept in sync after every render. Lets the keyword
  // updater read the latest committed state synchronously (instead of a
  // stale render closure) and compose multiple edits within the same tick.
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Updater for forbidden_topic_keywords. Reads/writes configRef so
  // concurrent chip-remove + blur-commit edits compose instead of
  // clobbering each other from stale closures. The setConfig updater is
  // kept pure (no setState inside it) — change detection and the dirty
  // flag are computed here, outside React's updater, so StrictMode's
  // double-invocation can't fire side effects twice. Marks dirty only
  // when the list actually changes (focus-blur on an empty input is a
  // no-op and won't reveal the Save button).
  const updateForbiddenKeywords = useCallback(
    (transform: (current: string[]) => string[]) => {
      const current = configRef.current.forbidden_topic_keywords || [];
      const next = transform(current);
      if (
        next.length === current.length &&
        next.every((k, i) => k === current[i])
      ) {
        return;
      }
      const nextConfig = {
        ...configRef.current,
        forbidden_topic_keywords: next,
      };
      configRef.current = nextConfig;
      setConfig(nextConfig);
      setConfigDirty(true);
    },
    [],
  );

  const validateFrameAncestorsInput = useCallback((raw: string): boolean => {
    const tokens = parseFrameAncestorsInput(raw);
    if (tokens.length === 0) {
      setFrameAncestorsError(null);
      return true;
    }
    const invalid = tokens.find((token) => !isValidFrameAncestorHost(token));
    if (invalid) {
      setFrameAncestorsError(
        `"${invalid}" is not a valid hostname. Use bare hosts like discovery.org or *.example.com.`,
      );
      return false;
    }
    setFrameAncestorsError(null);
    return true;
  }, []);

  const frameAncestorsFromInput = useCallback((raw: string): string[] => {
    return parseFrameAncestorsInput(raw)
      .map((token) => normalizeFrameAncestorToken(token))
      .filter((token): token is string => token !== null);
  }, []);

  const handleSaveConfig = useCallback(async () => {
    // Block save on outstanding validation errors and surface the offending
    // section: open Customize Appearance (where font fields live) so the now-
    // visible inline error is reachable instead of being hidden by collapse.
    if (hasFontErrors) {
      setAppearanceOpenSignal((n) => n + 1);
      notification.error('Fix the highlighted validation errors before saving');
      return;
    }
    if (!validateFrameAncestorsInput(frameAncestorsInput)) {
      setFrameAncestorsOpenSignal((n) => n + 1);
      notification.error('Fix the highlighted validation errors before saving');
      return;
    }
    // Search Settings (and its numeric inputs) only render in search mode, so
    // ignore any stale number errors left over from a previous search-mode
    // edit when the embed is now a chat — they'd otherwise block the save with
    // no visible field or error to fix.
    if (config.embed_type === 'search' && hasNumberErrors) {
      setSearchOpenSignal((n) => n + 1);
      notification.error('Fix the highlighted validation errors before saving');
      return;
    }
    // Fold any pending keyword input that the user typed but never
    // committed (clicking Save bypasses onBlur's setConfig commit; React
    // 18 batches the blur-driven update behind the click handler, so a
    // stale `config` would otherwise reach the API). Compute the
    // effective config synchronously, persist that, and then sync state.
    const pending = forbiddenKeywordInput;
    const normalizedFrameAncestors =
      frameAncestorsFromInput(frameAncestorsInput);
    const effectiveConfig: Partial<ExternalChatConfig> = pending.trim()
      ? {
          ...config,
          frame_ancestors: normalizedFrameAncestors,
          forbidden_topic_keywords: addForbiddenKeyword(
            config.forbidden_topic_keywords || [],
            pending,
          ),
        }
      : {
          ...config,
          frame_ancestors: normalizedFrameAncestors,
        };
    if (pending.trim()) {
      setConfig(effectiveConfig);
      setForbiddenKeywordInput('');
    }
    // Persist external_chat_config (per-field visual/search settings) and,
    // when dirty, the top-level external_chat_filters list via PATCH
    // /collections. Run in parallel — they touch independent fields.
    //
    // allSettled, not all: the two halves save independently, so a rejection
    // from one must not discard the other's outcome. Apply the successful half
    // either way so a partial failure doesn't force the user to re-enter a
    // clean draft.
    const [configResult, filtersResult] = await Promise.allSettled([
      saveConfig.mutateAsync(effectiveConfig),
      filtersDirty
        ? updateCollection({
            id: collectionId,
            externalChatFilters: filtersDraft,
          })
        : Promise.resolve(undefined),
    ]);

    const configOk = configResult.status === 'fulfilled';
    const filtersError =
      filtersResult.status === 'rejected'
        ? 'Failed to save filters'
        : filtersResult.value?.detail;

    if (configOk) {
      setConfigDirty(false);
      setPreviewConfig(effectiveConfig);
    }
    if (filtersDirty && !filtersError) {
      setFiltersDirty(false);
      onCollectionUpdated?.();
    }

    if (!configOk) {
      reportError(
        configResult.reason,
        'save-external-chat-config',
        'Failed to save configuration',
      );
      return;
    }
    if (filtersResult.status === 'rejected') {
      reportError(
        filtersResult.reason,
        'save-external-chat-filters',
        'Failed to save filters',
      );
      return;
    }
    if (filtersError) {
      notification.error(filtersError);
      return;
    }

    notification.success('Configuration saved');
  }, [
    collectionId,
    config,
    filtersDirty,
    filtersDraft,
    forbiddenKeywordInput,
    hasFontErrors,
    hasFrameAncestorsError,
    frameAncestorsFromInput,
    frameAncestorsInput,
    validateFrameAncestorsInput,
    hasNumberErrors,
    notification,
    onCollectionUpdated,
    reportError,
    saveConfig,
  ]);

  const handleResetInstructions = useCallback(() => {
    setConfig((prev) => ({
      ...prev,
      custom_instructions: '',
      summary_instructions_mode: 'append',
      forbidden_topic_keywords: [],
    }));
    // Drop any keyword typed but not yet committed — otherwise
    // handleSaveConfig would fold it back into the just-reset config.
    setForbiddenKeywordInput('');
    setConfigDirty(true);
  }, []);

  // Instruction version history. True when the Textarea draft
  // differs from the last-saved/activated instructions — gates the
  // "unsaved changes" confirm on Activate.
  const instructionsDirty =
    (config.custom_instructions || '') !==
    (previewConfig.custom_instructions || '');
  const atVersionCap = versions.length >= 20;

  const handleOpenSaveVersion = useCallback(() => {
    setVersionNameDraft('');
    setSaveVersionModalOpen(true);
  }, []);

  const handleSaveVersion = useCallback(async () => {
    try {
      await createVersion.mutateAsync({
        prompt: config.custom_instructions || '',
        name: versionNameDraft.trim() || undefined,
      });
      setSaveVersionModalOpen(false);
      setVersionNameDraft('');
      notification.success('Instructions saved as a version');
    } catch (err) {
      reportError(err, 'save-instruction-version', 'Failed to save version');
    }
  }, [
    config.custom_instructions,
    createVersion,
    notification,
    reportError,
    versionNameDraft,
  ]);

  const handleActivateVersion = useCallback(
    async (version: PromptVersion) => {
      try {
        const result = await activateVersion.mutateAsync(version.prompt_id);
        // CRITICAL: apply ONLY `custom_instructions` from the server's
        // authoritative response to `config`/`previewConfig` — activation only
        // changes that one field server-side. Spreading the whole config would
        // clobber any unsaved edits the user has to other fields (colors,
        // search settings) with stale server state. Writing it here rather than
        // leaving it to the config refetch is deliberate: activation is
        // confirmed precisely when the draft is dirty, and a dirty draft
        // suppresses re-seeding. We leave `configDirty` untouched: other-field
        // dirtiness must survive activation, and if only the instructions were
        // dirty, a stale-true flag just leaves Save enabled — clicking it
        // re-saves identical values, which is harmless.
        // A 200 means the server activated the version, so treat a body that
        // omits `config` as success and fall back to the prompt we asked it to
        // activate. Failing here instead would leave the editor showing the
        // old text against freshly-activated server state, and the next Save
        // Configuration would push that stale draft back over it.
        if (!result.config) {
          Sentry.captureException(
            new Error('activate-instruction-version returned no config'),
            {
              extra: { context: 'activate-instruction-version', collectionId },
            },
          );
        }
        const activated = result.config?.custom_instructions ?? version.prompt;
        setConfig((prev) => ({ ...prev, custom_instructions: activated }));
        setPreviewConfig((prev) => ({
          ...prev,
          custom_instructions: activated,
        }));
        notification.success('Version activated');
      } catch (err) {
        reportError(
          err,
          'activate-instruction-version',
          'Failed to activate version',
        );
      }
    },
    [activateVersion, collectionId, notification, reportError],
  );

  // Activating overwrites `custom_instructions` live on the server. If the
  // editor has unsaved edits, confirm first so they aren't silently
  // discarded — otherwise activate immediately.
  const handleRequestActivateVersion = useCallback(
    (version: PromptVersion) => {
      if (instructionsDirty) {
        setVersionToConfirmActivate(version);
        return;
      }
      void handleActivateVersion(version);
    },
    [handleActivateVersion, instructionsDirty],
  );

  const handleDeleteVersion = useCallback(
    async (version: PromptVersion) => {
      try {
        await deleteVersion.mutateAsync(version.prompt_id);
        notification.success('Version deleted');
      } catch (err) {
        reportError(
          err,
          'delete-instruction-version',
          'Failed to delete version',
        );
      }
    },
    [deleteVersion, notification, reportError],
  );

  // Determine the active key for preview & snippet
  const activeKey = useMemo(() => keys[0]?.api_key || '', [keys]);

  // Build embed code snippet (iframe-based) — uses previewConfig so it only
  // updates after save, not on every keystroke.
  const embedSnippet = useMemo(
    () =>
      buildEmbedSnippet({
        config: previewConfig,
        activeKey,
        collectionName,
        orgId,
        collectionId,
        externalChatFilters,
      }),
    [
      previewConfig,
      collectionId,
      collectionName,
      orgId,
      activeKey,
      externalChatFilters,
    ],
  );

  const handleCopySnippet = useCallback(() => {
    navigator.clipboard.writeText(embedSnippet).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => notification.error('Failed to copy to clipboard'),
    );
  }, [embedSnippet, notification]);

  const handleCopyNewKey = useCallback(() => {
    if (!newlyCreatedKey) return;
    navigator.clipboard.writeText(newlyCreatedKey).then(
      () => setKeyCopied(true),
      () => notification.error('Failed to copy to clipboard'),
    );
  }, [newlyCreatedKey, notification]);

  // Preview URL for the iframe — uses previewConfig so it only reloads after save
  const previewUrl = useMemo(
    () =>
      buildPreviewUrl({
        config: previewConfig,
        activeKey,
        collectionName,
        orgId,
        collectionId,
        externalChatFilters,
      }),
    [
      orgId,
      collectionId,
      activeKey,
      previewConfig,
      collectionName,
      externalChatFilters,
    ],
  );

  if (configLoading || keysLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  // No API keys CTA state — prompt user to generate their first key
  if (keys.length === 0 && !newlyCreatedKey) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center justify-center py-16'>
          <Card className='w-full max-w-md text-center'>
            <CardContent className='flex flex-col items-center gap-4 pt-8 pb-8'>
              <div className='flex size-14 items-center justify-center rounded-full bg-muted'>
                <KeyRound className='size-7 text-muted-foreground' />
              </div>
              <div className='space-y-2'>
                <h3 className='text-lg font-semibold'>
                  Get Started with External Chat
                </h3>
                <p className='text-sm text-muted-foreground max-w-sm'>
                  Generate an embed API key to preview and configure your
                  external chat widget
                </p>
              </div>
              <Button onClick={handleCreateKey} disabled={creatingKey}>
                {creatingKey && (
                  <Loader2 className='size-4 animate-spin mr-1' />
                )}
                Generate API Key
              </Button>
            </CardContent>
          </Card>
        </div>
        <SearchQueryExportSection orgId={orgId} collectionId={collectionId} />
      </div>
    );
  }

  return (
    <>
      <div className='mb-4 flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
        <MessageSquareShare className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
        <p className='text-sm leading-relaxed text-muted-foreground'>
          Embed a chat widget on your website so external users can ask
          questions against this collection. Configure the look and feel,
          generate an API key, and copy the embed snippet into your site.
        </p>
      </div>
      <div className='flex flex-col xl:flex-row gap-6'>
        {/* Left column: configuration panels */}
        <div className='flex-1 min-w-0 space-y-6'>
          {/* API Key callout bar */}
          <div className='flex items-center gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-2.5'>
            <KeyRound className='size-4 shrink-0 text-muted-foreground' />
            <span className='flex-1 text-sm text-muted-foreground'>
              {keys.length === 0
                ? 'No embed API keys yet'
                : `${keys.length} active API key${keys.length > 1 ? 's' : ''}`}
            </span>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setKeysModalOpen(true)}
            >
              Manage Keys
            </Button>
          </div>

          {/* Embed Configuration */}
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base font-semibold'>
                Embed Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              {/* General — core identity & behavior, open by default */}
              <ConfigSection title='General' defaultOpen>
                <div className='space-y-2'>
                  <Label>Embed Type</Label>
                  <Select
                    value={config.embed_type || 'chat'}
                    onValueChange={(v) => {
                      handleConfigChange('embed_type', v);
                      // Leaving search mode unmounts the Search Settings inputs,
                      // so any inline numeric errors become invisible. Clear them
                      // and coerce any out-of-range value back to a valid default
                      // so a stale invalid number (e.g. results_per_page: 0) can't
                      // ride along on a chat-mode save.
                      if (v !== 'search') {
                        if (numberErrors.results_per_page) {
                          handleConfigChange('results_per_page', 10);
                        }
                        if (numberErrors.score_threshold) {
                          handleConfigChange('score_threshold', 0.5);
                        }
                        if (numberErrors.summary_top_k) {
                          handleConfigChange('summary_top_k', 15);
                        }
                        setNumberErrors({});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='chat'>Chat</SelectItem>
                      <SelectItem value='search'>
                        Search (Dive Deeper)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label>AI Model</Label>
                  {(() => {
                    // The saved model may resolve before the registry request
                    // does, or the registry may no longer list it. Without a
                    // matching SelectItem the trigger renders blank, so emit a
                    // fallback item carrying the raw value as its own label.
                    const savedModel = config.llm_model;
                    const isKnownModel =
                      !savedModel ||
                      llmModels.anthropic.some((m) => m.value === savedModel) ||
                      llmModels.openai.some((m) => m.value === savedModel);
                    return (
                      <Select
                        value={savedModel || DEFAULT_MODEL_VALUE}
                        onValueChange={(v) => {
                          if (v === DEFAULT_MODEL_VALUE) {
                            // Explicit null (not undefined) so JSON.stringify
                            // keeps the keys in the PATCH body — the backend
                            // treats null as a delete and reverts the embed to
                            // the platform default.
                            handleConfigChange('llm_model', null);
                            handleConfigChange('llm_provider', null);
                          } else {
                            const foundInAnthropic = llmModels.anthropic.find(
                              (m) => m.value === v,
                            );
                            const foundInOpenai = llmModels.openai.find(
                              (m) => m.value === v,
                            );
                            const selected = foundInAnthropic ?? foundInOpenai;
                            handleConfigChange('llm_model', v);
                            // When the model isn't in the loaded registry (the
                            // fallback item for a saved/deprecated id), don't
                            // guess 'anthropic' — that would persist the wrong
                            // provider for a deprecated OpenAI model. Keep the
                            // already-stored provider, which the BE re-derives
                            // from llm_model on save anyway.
                            const resolvedProvider =
                              selected?.provider ??
                              (foundInOpenai
                                ? 'openai'
                                : (config.llm_provider ?? undefined));
                            if (resolvedProvider !== undefined) {
                              handleConfigChange(
                                'llm_provider',
                                resolvedProvider,
                              );
                            }
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={DEFAULT_MODEL_VALUE}>
                            Default (Claude Sonnet)
                          </SelectItem>
                          {!isKnownModel && savedModel && (
                            <SelectItem value={savedModel}>
                              {savedModel}
                            </SelectItem>
                          )}
                          {llmModels.anthropic.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>Anthropic</SelectLabel>
                              {llmModels.anthropic.map((m) => (
                                <SelectItem key={m.value} value={m.value}>
                                  {m.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {llmModels.openai.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>OpenAI</SelectLabel>
                              {llmModels.openai.map((m) => (
                                <SelectItem key={m.value} value={m.value}>
                                  {m.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='chat-title'>Title</Label>
                  <Input
                    id='chat-title'
                    value={config.title || ''}
                    onChange={(e) =>
                      handleConfigChange('title', e.target.value)
                    }
                    placeholder={collectionName || 'Chat'}
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='greeting'>Greeting Message</Label>
                  <Input
                    id='greeting'
                    value={config.greeting || ''}
                    onChange={(e) =>
                      handleConfigChange('greeting', e.target.value)
                    }
                    placeholder='Ask me anything!'
                  />
                </div>

                <div className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    id='allow-citation-links'
                    checked={config.allow_external_citation_links !== false}
                    onChange={(e) =>
                      handleConfigChange(
                        'allow_external_citation_links',
                        e.target.checked,
                      )
                    }
                    className='size-4 rounded border'
                  />
                  <Label htmlFor='allow-citation-links'>
                    Allow external site citation links
                    <InfoTip text='When off, AI summaries may only cite URLs whose host matches a source domain present on the returned chunks. Off-host links are stripped from both chunk context and the streamed summary.' />
                  </Label>
                </div>
              </ConfigSection>

              <ConfigSection
                title='Frame ancestors'
                hasError={hasFrameAncestorsError}
                openSignal={frameAncestorsOpenSignal}
              >
                <div className='space-y-2'>
                  <Label htmlFor='frame-ancestors'>
                    Parent sites
                    <InfoTip text='Hostnames that may embed this widget in an iframe. Empty = any site. This is not the Manage Keys Origin allowlist. Include both apex and www if you use both. Admin preview on this app still works.' />
                  </Label>
                  <Input
                    id='frame-ancestors'
                    value={frameAncestorsInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFrameAncestorsInput(raw);
                      validateFrameAncestorsInput(raw);
                      handleConfigChange(
                        'frame_ancestors',
                        frameAncestorsFromInput(raw),
                      );
                    }}
                    onBlur={(e) => validateFrameAncestorsInput(e.target.value)}
                    placeholder='discovery.org, www.discovery.org'
                    aria-invalid={hasFrameAncestorsError}
                  />
                  {frameAncestorsError ? (
                    <p className='text-xs text-destructive'>
                      {frameAncestorsError}
                    </p>
                  ) : (
                    <p className='text-xs text-muted-foreground'>
                      Comma-separated hostnames. Empty allows any parent site.
                      Wildcards like *.example.com are supported.
                    </p>
                  )}
                </div>
              </ConfigSection>

              {/* Layout — embed sizing */}
              <ConfigSection title='Layout'>
                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-2'>
                    <Label htmlFor='embed-height'>Embed Height</Label>
                    <Input
                      id='embed-height'
                      value={config.embed_height || ''}
                      onChange={(e) =>
                        handleConfigChange('embed_height', e.target.value)
                      }
                      placeholder='600px'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='content-max-width'>Content Width</Label>
                    <Select
                      value={config.content_max_width || '672px'}
                      onValueChange={(v) =>
                        handleConfigChange('content_max_width', v)
                      }
                    >
                      <SelectTrigger id='content-max-width'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='672px'>Narrow (672px)</SelectItem>
                        <SelectItem value='800px'>Medium (800px)</SelectItem>
                        <SelectItem value='960px'>Wide (960px)</SelectItem>
                        <SelectItem value='100%'>Full Width</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </ConfigSection>

              {/* Customize Appearance — collapsible */}
              <ConfigSection
                title='Customize Appearance'
                hasError={hasFontErrors}
                openSignal={appearanceOpenSignal}
              >
                <ColorField
                  id='primary-color'
                  label='Primary Color'
                  value={config.primary_color || ''}
                  defaultValue='#2563eb'
                  showDefaultInTextInput
                  onChange={(v) => handleConfigChange('primary_color', v)}
                />

                <ColorField
                  id='font-color'
                  label='Font Color'
                  value={config.font_color || ''}
                  defaultValue='#111827'
                  showDefaultInTextInput
                  onChange={(v) => handleConfigChange('font_color', v)}
                />

                <ColorField
                  id='bg-color'
                  label='Background Color'
                  value={config.background_color || ''}
                  defaultValue='#ffffff'
                  showDefaultInTextInput
                  onChange={(v) => handleConfigChange('background_color', v)}
                />

                <ColorField
                  id='link-color'
                  label='Link Color'
                  value={config.link_color || ''}
                  defaultValue='#2563eb'
                  showDefaultInTextInput
                  onChange={(v) => handleConfigChange('link_color', v)}
                />

                <FontField
                  id='header-font'
                  label={
                    <>
                      Header Font
                      <InfoTip text='Google Font family for titles and headings (e.g. "Crimson Text"). Validated against Google Fonts on blur.' />
                    </>
                  }
                  fontValue={config.header_font || ''}
                  weightValue={config.header_font_weight || ''}
                  placeholder='e.g. Crimson Text'
                  error={fontErrors.header_font}
                  onFontChange={(v) => handleConfigChange('header_font', v)}
                  onWeightChange={(v) =>
                    handleConfigChange('header_font_weight', v)
                  }
                  onBlur={() => handleFontBlur('header_font')}
                />

                <FontField
                  id='body-font'
                  label={
                    <>
                      Body Font
                      <InfoTip text='Google Font family for body text (e.g. "Open Sans"). Validated against Google Fonts on blur.' />
                    </>
                  }
                  fontValue={config.body_font || ''}
                  weightValue={config.body_font_weight || ''}
                  placeholder='e.g. Open Sans'
                  error={fontErrors.body_font}
                  onFontChange={(v) => handleConfigChange('body_font', v)}
                  onWeightChange={(v) =>
                    handleConfigChange('body_font_weight', v)
                  }
                  onBlur={() => handleFontBlur('body_font')}
                />

                <ColorField
                  id='search-bg-color'
                  label={
                    <>
                      Search Bar Background
                      <InfoTip text='Background color of the search input bar' />
                    </>
                  }
                  value={config.search_bg_color || ''}
                  defaultValue='#f9fafb'
                  onChange={(v) => handleConfigChange('search_bg_color', v)}
                />

                <ColorField
                  id='search-text-color'
                  label={
                    <>
                      Search Bar Text Color
                      <InfoTip text='Text color of the search input' />
                    </>
                  }
                  value={config.search_text_color || ''}
                  defaultValue='#111827'
                  onChange={(v) => handleConfigChange('search_text_color', v)}
                />

                <div className='space-y-2'>
                  <Label htmlFor='border-radius'>
                    Border Radius
                    <InfoTip text='Corner rounding for cards, inputs, and buttons. Set to "0" for sharp corners.' />
                  </Label>
                  <Input
                    id='border-radius'
                    value={config.border_radius || ''}
                    onChange={(e) =>
                      handleConfigChange('border_radius', e.target.value)
                    }
                    placeholder='8px (default)'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='custom-css'>
                    Custom CSS
                    <InfoTip text='Custom CSS injected into the embed iframe. Use to override any embed styling for seamless site integration.' />
                  </Label>
                  <Textarea
                    id='custom-css'
                    value={config.custom_css || ''}
                    onChange={(e) =>
                      handleConfigChange('custom_css', e.target.value)
                    }
                    placeholder={`.search-input { border: 2px solid #ccc; }\n.result-card { box-shadow: none; }`}
                    rows={4}
                    className='font-mono text-xs'
                  />
                </div>
              </ConfigSection>

              {/* Search Settings — only when mode = search */}
              {config.embed_type === 'search' && (
                <ConfigSection
                  title='Search Settings'
                  defaultOpen
                  hasError={hasNumberErrors}
                  openSignal={searchOpenSignal}
                >
                  <div className='space-y-2'>
                    <Label>
                      Results Per Page
                      <InfoTip text='Number of search results shown per page' />
                    </Label>
                    <Input
                      type='number'
                      min={1}
                      max={50}
                      step={1}
                      aria-invalid={!!numberErrors.results_per_page}
                      value={config.results_per_page ?? 10}
                      onChange={(e) => {
                        handleConfigChange(
                          'results_per_page',
                          Number(e.target.value),
                        );
                        validateNumberField(
                          'results_per_page',
                          e.target.value,
                          { min: 1, max: 50, integer: true },
                        );
                      }}
                    />
                    {numberErrors.results_per_page && (
                      <p className='text-xs text-destructive'>
                        {numberErrors.results_per_page}
                      </p>
                    )}
                  </div>

                  <div className='space-y-2'>
                    <Label>
                      Score Threshold
                      <InfoTip text='Minimum relevance score between 0 and 1. Higher values return fewer but more relevant results' />
                    </Label>
                    <Input
                      type='number'
                      min={0}
                      max={1}
                      step={0.05}
                      aria-invalid={!!numberErrors.score_threshold}
                      value={config.score_threshold ?? 0.5}
                      onChange={(e) => {
                        handleConfigChange(
                          'score_threshold',
                          Number(e.target.value),
                        );
                        validateNumberField('score_threshold', e.target.value, {
                          min: 0,
                          max: 1,
                          integer: false,
                        });
                      }}
                    />
                    {numberErrors.score_threshold && (
                      <p className='text-xs text-destructive'>
                        {numberErrors.score_threshold}
                      </p>
                    )}
                  </div>

                  <div className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      id='recency-boost'
                      checked={config.enable_recency_boost === true}
                      onChange={(e) =>
                        handleConfigChange(
                          'enable_recency_boost',
                          e.target.checked,
                        )
                      }
                      className='size-4 rounded border'
                    />
                    <Label htmlFor='recency-boost'>
                      Recency Boost
                      <InfoTip text='Boost newer content higher in results. Useful for news-oriented collections' />
                    </Label>
                  </div>

                  <div className='space-y-2'>
                    <Label>
                      Summary Chunks
                      <InfoTip text='Number of top search results used to generate the AI summary and seed dive-deeper chat context' />
                    </Label>
                    <Input
                      type='number'
                      min={1}
                      max={50}
                      step={1}
                      aria-invalid={!!numberErrors.summary_top_k}
                      value={config.summary_top_k ?? 15}
                      onChange={(e) => {
                        handleConfigChange(
                          'summary_top_k',
                          Number(e.target.value),
                        );
                        validateNumberField('summary_top_k', e.target.value, {
                          min: 1,
                          max: 50,
                          integer: true,
                        });
                      }}
                    />
                    {numberErrors.summary_top_k && (
                      <p className='text-xs text-destructive'>
                        {numberErrors.summary_top_k}
                      </p>
                    )}
                  </div>
                </ConfigSection>
              )}

              {/* Filters — which Qdrant indexes surface as iframe filter
                  widgets. Independent of upload-editor exposed attrs. */}
              <ConfigSection title='Filters'>
                <p className='text-xs text-muted-foreground'>
                  Pre-declare filter widgets shown to end-users in the embedded
                  chat iframe. Filters target existing Qdrant payload indexes on
                  this collection.
                </p>
                <ExternalChatFiltersSection
                  availableFields={availableFilterFields}
                  filters={filtersDraft}
                  onChange={handleFiltersChange}
                  disabled={savingConfig}
                  collectionId={collectionId}
                  orgId={orgId}
                  isLoading={collectionInfoLoading}
                  isError={collectionInfoError}
                />
              </ConfigSection>

              {/* Agent Instructions */}
              <ConfigSection title='Agent Instructions'>
                <div className='flex items-center justify-between'>
                  <p className='text-xs text-muted-foreground'>
                    Customize how the AI agent responds. Leave empty to use the
                    default conversational template for &quot;{collectionName}
                    &quot;.
                  </p>
                  <div className='flex items-center gap-2 shrink-0'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setVersionsModalOpen(true)}
                    >
                      <History className='size-3.5 mr-1' />
                      Version history
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleResetInstructions}
                    >
                      <RotateCcw className='size-3.5 mr-1' />
                      Reset to default
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={config.custom_instructions || ''}
                  onChange={(e) =>
                    handleConfigChange('custom_instructions', e.target.value)
                  }
                  placeholder={`${DEFAULT_INSTRUCTIONS_PREFIX} answering questions using information from ${collectionName}.\n\nYou have access to search tools. Always search the "${collectionName}" collection when looking for information...`}
                  rows={8}
                  className='font-mono text-xs'
                />
                <div className='mt-2 flex items-center gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleOpenSaveVersion}
                    disabled={
                      versionsLoading ||
                      atVersionCap ||
                      !config.custom_instructions?.trim()
                    }
                  >
                    <Save className='size-3.5 mr-1' />
                    Save as version
                  </Button>
                  {atVersionCap && (
                    <span className='text-xs text-muted-foreground'>
                      Version limit reached (20). Delete old versions to save
                      more.
                    </span>
                  )}
                </div>

                {/* Override drops the platform default entirely so customer
                  rules can't be contradicted by it; the BE keeps a minimal
                  safety floor either way. */}
                <div className='mt-4 space-y-2'>
                  <Label className='text-xs font-semibold'>
                    How should custom instructions interact with the platform
                    default?
                  </Label>
                  <RadioGroup
                    value={config.summary_instructions_mode || 'append'}
                    onValueChange={(value) =>
                      handleConfigChange(
                        'summary_instructions_mode',
                        value as 'append' | 'override',
                      )
                    }
                    className='gap-2'
                  >
                    <div className='flex items-start gap-2'>
                      <RadioGroupItem
                        value='append'
                        id='summary-instructions-mode-append'
                        className='mt-0.5'
                      />
                      <Label
                        htmlFor='summary-instructions-mode-append'
                        className='text-xs font-normal leading-snug cursor-pointer'
                      >
                        <span className='font-medium'>Append (default)</span> —
                        your rules layered on top of the platform defaults (warm
                        tone, emoji guidance, source-grounding posture). Use
                        when your rules complement the defaults.
                      </Label>
                    </div>
                    <div className='flex items-start gap-2'>
                      <RadioGroupItem
                        value='override'
                        id='summary-instructions-mode-override'
                        className='mt-0.5'
                      />
                      <Label
                        htmlFor='summary-instructions-mode-override'
                        className='text-xs font-normal leading-snug cursor-pointer'
                      >
                        <span className='font-medium'>Override</span> — your
                        rules replace the platform defaults entirely. Use when
                        your rules conflict with the defaults (e.g. no emojis,
                        no first person).
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Scrubs matching sentences from both source chunks and the
                  streamed summary -- for operators whose forbidden topics still
                  appear in their own corpus. */}
                <div className='mt-4 space-y-2'>
                  <Label className='text-xs font-semibold'>
                    Forbidden topic keywords
                  </Label>
                  <p className='text-xs text-muted-foreground'>
                    Sentences containing these words are redacted from both
                    source chunks and the AI summary. Case-insensitive,
                    whole-word match. Use to enforce topic exclusions that your
                    source corpus still discusses.
                  </p>
                  <div className='flex flex-wrap gap-1.5'>
                    {(config.forbidden_topic_keywords || []).map((kw) => (
                      <span
                        key={kw}
                        className='inline-flex items-center gap-1 rounded-md border bg-muted px-2 py-0.5 text-xs'
                      >
                        {kw}
                        <button
                          type='button'
                          aria-label={`Remove ${kw}`}
                          className='hover:text-destructive'
                          onClick={() =>
                            updateForbiddenKeywords((cur) =>
                              removeForbiddenKeyword(cur, kw),
                            )
                          }
                        >
                          <X className='size-3' />
                        </button>
                      </span>
                    ))}
                  </div>
                  <Input
                    value={forbiddenKeywordInput}
                    onChange={(e) => setForbiddenKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        updateForbiddenKeywords((cur) =>
                          addForbiddenKeyword(cur, forbiddenKeywordInput),
                        );
                        setForbiddenKeywordInput('');
                      } else if (
                        e.key === 'Backspace' &&
                        !forbiddenKeywordInput
                      ) {
                        updateForbiddenKeywords((cur) =>
                          popLastForbiddenKeyword(cur),
                        );
                      }
                    }}
                    onBlur={() => {
                      if (!forbiddenKeywordInput.trim()) return;
                      updateForbiddenKeywords((cur) =>
                        addForbiddenKeyword(cur, forbiddenKeywordInput),
                      );
                      setForbiddenKeywordInput('');
                    }}
                    placeholder='Type a keyword and press Enter'
                    className='text-xs h-8'
                  />
                </div>

                {configDirty && (
                  <Button
                    onClick={handleSaveConfig}
                    disabled={configWriteInFlight}
                    className='mt-3'
                    size='sm'
                  >
                    {savingConfig ? (
                      <Loader2 className='size-4 animate-spin mr-1' />
                    ) : (
                      <Save className='size-4 mr-1' />
                    )}
                    Save
                  </Button>
                )}
              </ConfigSection>

              {/* Embed Code Snippet */}
              <ConfigSection title='Embed Code'>
                <div className='flex items-center justify-between'>
                  <p className='text-xs text-muted-foreground'>
                    Add this code to your website to embed the chat widget.
                  </p>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleCopySnippet}
                  >
                    {copied ? (
                      <>
                        <Check className='size-3.5 mr-1' />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className='size-3.5 mr-1' />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto'>
                  <code>{embedSnippet}</code>
                </pre>
              </ConfigSection>

              <SearchQueryExportSection
                orgId={orgId}
                collectionId={collectionId}
              />

              {/* API Access documentation */}
              <ConfigSection title='API Access'>
                <ApiDocsSection
                  orgId={orgId}
                  collectionId={collectionId}
                  activeKey={activeKey}
                  bare
                />
              </ConfigSection>

              <Button
                onClick={handleSaveConfig}
                disabled={
                  (!configDirty && !filtersDirty) || configWriteInFlight
                }
                className='w-full'
              >
                {savingConfig ? (
                  <Loader2 className='size-4 animate-spin mr-2' />
                ) : (
                  <Save className='size-4 mr-2' />
                )}
                Save Configuration
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right column: sticky live preview */}
        {activeKey && previewUrl && (
          <div className='w-full xl:w-[420px] xl:shrink-0 xl:sticky xl:top-4 xl:self-start order-first xl:order-last'>
            <Card>
              <CardHeader className='pb-3'>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-base font-semibold'>
                    Live Preview
                  </CardTitle>
                  <a
                    href={previewUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
                  >
                    Open in new tab
                    <ExternalLink className='size-3' />
                  </a>
                </div>
              </CardHeader>
              <CardContent>
                <div className='rounded-lg border overflow-hidden bg-muted/20'>
                  <iframe
                    src={previewUrl}
                    title='External chat preview'
                    className='w-full border-0'
                    style={{ height: config.embed_height || '600px' }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <ManageKeysModal
        open={keysModalOpen}
        onOpenChange={setKeysModalOpen}
        keys={keys}
        creatingKey={creatingKey}
        onCreateKey={handleCreateKey}
        onRequestRevoke={setKeyToRevoke}
        onSaveSettings={handleUpdateKeySettings}
      />

      <RevealNewKeyModal
        apiKey={newlyCreatedKey}
        keyCopied={keyCopied}
        onClose={() => setNewlyCreatedKey(null)}
        onCopy={handleCopyNewKey}
      />

      <RevokeKeyModal
        apiKey={keyToRevoke}
        onCancel={() => setKeyToRevoke(null)}
        onConfirm={(apiKey) => {
          handleRevokeKey(apiKey);
          setKeyToRevoke(null);
        }}
      />

      <InstructionVersionsModal
        open={versionsModalOpen}
        onOpenChange={setVersionsModalOpen}
        versions={versions}
        loading={versionsLoading}
        loadFailed={versionsLoadFailed}
        retrying={versionsFetching}
        onRetry={refetchVersions}
        activateDisabled={configWriteInFlight}
        activatingId={activatingVersionId}
        onRequestActivate={handleRequestActivateVersion}
        onRequestDelete={setVersionToDelete}
      />

      <SaveInstructionVersionModal
        open={saveVersionModalOpen}
        onOpenChange={setSaveVersionModalOpen}
        name={versionNameDraft}
        onNameChange={setVersionNameDraft}
        saving={savingVersion}
        onSave={handleSaveVersion}
      />

      <DeleteVersionModal
        version={versionToDelete}
        onCancel={() => setVersionToDelete(null)}
        onConfirm={(version) => {
          handleDeleteVersion(version);
          setVersionToDelete(null);
        }}
      />

      <ActivateVersionModal
        version={versionToConfirmActivate}
        onCancel={() => setVersionToConfirmActivate(null)}
        onConfirm={(version) => {
          void handleActivateVersion(version);
          setVersionToConfirmActivate(null);
        }}
      />
    </>
  );
};
